import type { FirstAccountSetupState, ServerSettings } from "@cmmd-forge/contracts";

import { COMPLETED_FIRST_ACCOUNT_SETUP_STATE } from "../../lib/firstAccountSetup";

/**
 * Setup-completion side of finishing the welcome wizard.
 *
 * Finishing the wizard has to mean the same thing as finishing the
 * first-account-setup page, or a fresh browser that chose its first project in
 * the wizard gets bounced through the setup page afterwards. The two surfaces
 * disagree on where the completion lives: a cloud-control surface keeps it in
 * CMMD account preferences, while a local execution plane keeps it in server
 * settings. Both paths are awaitable here and must actually persist before
 * the caller writes the onboarding flag: a completion timestamp without a
 * persisted setup state would let a reload skip straight past the gate into
 * an unfinished setup.
 */
export interface WelcomeCompletionInput {
  readonly useCmmdSetupState: boolean;
  readonly updateServerSettings: (patch: {
    firstAccountSetup: FirstAccountSetupState;
  }) => Promise<ServerSettings>;
  readonly onServerSettingsPersisted: (settings: ServerSettings) => void;
  readonly publishCmmdSetupCompletion: () => Promise<void>;
}

export async function completeWelcomeSetupState(input: WelcomeCompletionInput): Promise<boolean> {
  if (!input.useCmmdSetupState) {
    try {
      const next = await input.updateServerSettings({
        firstAccountSetup: COMPLETED_FIRST_ACCOUNT_SETUP_STATE,
      });
      input.onServerSettingsPersisted(next);
      return true;
    } catch {
      // The caller toasts and keeps the wizard open; a retry re-runs the whole
      // completion, which is idempotent.
      return false;
    }
  }
  try {
    await input.publishCmmdSetupCompletion();
    return true;
  } catch {
    return false;
  }
}
